import React, { useState } from 'react';
import Input from '@ajat/design-system/Input';
import { ComponentPage, DemoCard } from '../shared';

const OPTIONS = [
  { value: 'option1', label: 'Option 1' },
  { value: 'option2', label: 'Option 2' },
  { value: 'option3', label: 'Option 3' },
  { value: 'option4', label: 'Option 4' },
];

const SEARCH_OPTIONS = [
  { value: 'valeria', label: 'Valeria' },
  { value: 'vallon', label: 'Vallon' },
  { value: 'value', label: 'Value' },
  { value: 'avalanche', label: 'Avalanche' },
  { value: 'bovalar', label: 'Bovalar' },
];

// Full class strings, never interpolated — Tailwind's scanner only sees literals.
const Grid = ({ cols = 3, children }) => (
  <div
    className={
      cols === 2
        ? 'grid grid-cols-1 md:grid-cols-2 gap-[var(--space-ui-medium-m)]'
        : 'grid grid-cols-1 md:grid-cols-3 gap-[var(--space-ui-medium-m)]'
    }
  >
    {children}
  </div>
);

export const meta = {
  component: Input,
  title: 'Input',
  group: 'Forms',
  importPath: '@ajat/design-system/Input',
  figmaNode: '3637:10702',
  description:
    'One component covering every field in the system — text, password, textarea, dropdown, searchable, multiselect, date and action — with a floating label, an optional leading icon and a right-hand action stack that composes success, clear, reveal, chevron and action buttons in a fixed order.',
  props: [
    ['type', "'text' | 'password' | 'textarea' | 'dropdown' | 'select' | 'action' | 'date' | 'searchable' | 'multiselect' | 'email' | 'tel' | 'url' | 'number'", "'text'", 'Variant — drives which internals render'],
    ['label', 'string', "'Label'", 'Floating label, also the placeholder while empty'],
    ['value', 'string | number', '—', 'Controlled value'],
    ['defaultValue', 'string | number', '—', 'Initial value (uncontrolled)'],
    ['onChange', 'func', '—', 'Change handler — receives the native event'],
    ['onBlur', 'func', '—', 'Blur handler; also fires when the mobile keyboard closes'],
    ['onFocus', 'func', '—', 'Focus handler'],
    ['icon', 'node', 'null', 'Leading icon; a sensible default is picked per type'],
    ['showIcon', 'bool', 'true', 'Show the leading icon'],
    ['showOptionIcons', 'bool', 'true', 'Show icons inside dropdown options'],
    ['showSuccess', 'bool', 'false', 'Show the success checkmark'],
    ['showClear', 'bool', 'false', 'Show a clear (X) button when the field has a value'],
    ['helperText', 'string', "''", 'Helper line under the field'],
    ['error', 'string', "''", 'Error message — switches the field to its error state'],
    ['reserveMessageSpace', 'bool', 'false', 'Keep one message line under the field whether it holds helper text, nothing or the error — the content below never jumps'],
    ['disabled', 'bool', 'false', 'Disables the field'],
    ['onAction', 'func', 'null', 'Action-button handler — setting it alone turns the field into type="action"'],
    ['actionIcon', 'node', 'null', 'Icon for the action button (default ArrowRight)'],
    ['options', 'array', '[]', '[{ value, label, icon?, fallback? }] for dropdown / searchable / multiselect'],
    ['onSelect', 'func', 'null', 'Fires with the chosen option object (or array when multiple)'],
    ['multiple', 'bool', 'false', 'Multiselect mode — renders chosen options as tags'],
    ['searchable', 'bool', 'false', 'Type-to-filter with match highlighting'],
    ['rows', 'number', '4', 'Textarea rows'],
    ['resizable', 'bool', 'true', 'Allow textarea resizing'],
    ['mobileFocus', 'bool', 'false', 'Open a fullscreen focus sheet on mobile'],
    ['mobileFocusPlacement', "'top' | 'bottom'", "'top'", "Where focus mode puts the field: 'top' is the fullscreen sheet, 'bottom' a bar fixed above the keyboard"],
    ['onMobileFocusChange', 'func', 'null', 'fn(active) — fires as focus mode opens and closes, so the host can react (e.g. keep a 3D preview above the keyboard)'],
    ['mobileFocusReveal', 'object', 'null', '{ section, sticky, gap, align } — what to scroll back into view once the sheet closes, and the sticky element it must sit below. Without it: the wrapper itself, only when off-screen'],
    ['className', 'string', "''", 'Additional CSS class'],
    ['id', 'string', '—', 'Native id'],
    ['name', 'string', '—', 'Native name'],
    ['placeholder', 'string', '—', 'Native placeholder (the label doubles as one)'],
    ['autoComplete', 'string', '—', 'Native autocomplete'],
    ['required', 'bool', 'false', 'Native required'],
    ['readOnly', 'bool', 'false', 'Native readOnly — used by DatePicker to suppress the mobile keyboard'],
    ['maxLength', 'number', '—', 'Native maxLength'],
    ['minLength', 'number', '—', 'Native minLength'],
    ['pattern', 'string', '—', 'Native pattern'],
    ['ariaLabels', '{clear, showPassword, hidePassword, clearSelection, toggleDropdown, openCalendar, submit, remove}', 'English', "Screen-reader names of the built-in icon buttons, in the page's language; `remove` prefixes a tag's label"],
  ],
  propsNote:
    'Input forwards a ref to the underlying field, and spreads any remaining props onto it — onKeyDown, aria-* and data-* all pass through.',
  usage: [
    {
      title: 'Text, with validation',
      code: `import Input from '@ajat/design-system/Input';

<Input
  label="E-mail"
  type="email"
  value={form.email}
  onChange={(e) => setField('email', e.target.value)}
  onBlur={() => validate('email')}
  error={touched.email ? errors.email : ''}
  showClear
/>`,
    },
    {
      title: 'Searchable dropdown',
      code: `// Matches are highlighted as you type. An option flagged
// \`fallback\` renders only when nothing else matches — the
// "my school is not in the list" escape hatch.
<Input
  type="searchable"
  label="Vælg din skole"
  options={[
    ...schools.map(s => ({ value: s.slug, label: s.name })),
    { value: '__none', label: 'Min skole er ikke på listen', fallback: true },
  ]}
  onSelect={(option) => setSchool(option.value)}
/>`,
    },
    {
      title: 'Action input',
      code: `import { Percent, ArrowRight } from 'lucide-react';

// Passing onAction is enough — type="action" is implied
<Input
  label="Discount code"
  icon={<Percent size={20} />}
  actionIcon={<ArrowRight size={20} />}
  onAction={(value) => applyDiscount(value)}
/>`,
    },
  ],
};

export default function InputPage() {
  const [text, setText] = useState('');
  const [password, setPassword] = useState('');
  const [textarea, setTextarea] = useState('');
  const [dropdown, setDropdown] = useState('');
  const [action, setAction] = useState('');

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Text">
        <div className="space-y-[var(--space-ui-medium-m)]">
          <Grid>
            <Input label="With icon (default)" value={text} onChange={(e) => setText(e.target.value)} />
            <Input label="Without icon" showIcon={false} defaultValue="No icon input" />
            <Input label="With error" error="This field is required" defaultValue="Invalid input" />
          </Grid>
          <Grid>
            <Input label="Disabled" disabled defaultValue="Disabled value" />
            <Input label="With success" showSuccess defaultValue="Valid input" />
            <Input label="With clear" showClear defaultValue="Clearable" />
          </Grid>
        </div>
      </DemoCard>

      <DemoCard title="Password">
        <Grid>
          <Input type="password" label="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <Input type="password" label="With value" defaultValue="secretpassword" />
          <Input type="password" label="With error" error="Password too weak" defaultValue="123" />
        </Grid>
      </DemoCard>

      <DemoCard title="Textarea">
        <Grid cols={2}>
          <Input
            type="textarea"
            label="Description"
            value={textarea}
            onChange={(e) => setTextarea(e.target.value)}
            rows={4}
          />
          <Input
            type="textarea"
            label="With value"
            defaultValue="Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore."
            rows={4}
          />
        </Grid>
      </DemoCard>

      <DemoCard title="Dropdown">
        <Grid>
          <Input
            type="dropdown"
            label="With icons"
            value={dropdown}
            onChange={(e) => setDropdown(e.target.value)}
            options={OPTIONS}
          />
          <Input
            type="dropdown"
            label="Without icons"
            showIcon={false}
            showOptionIcons={false}
            defaultValue="Option 2"
            options={OPTIONS}
          />
          <Input type="dropdown" label="Disabled" disabled defaultValue="Option 1" options={OPTIONS} />
        </Grid>
      </DemoCard>

      <DemoCard title="Searchable" hint="type to filter — matches are highlighted">
        <Grid cols={2}>
          <Input type="searchable" label="With icons" options={SEARCH_OPTIONS} />
          <Input type="searchable" label="Without icons" showIcon={false} showOptionIcons={false} options={OPTIONS} />
        </Grid>
      </DemoCard>

      <DemoCard title="Multiselect" hint="chosen options render as removable tags">
        <Grid cols={2}>
          <Input type="multiselect" label="Pick several" options={OPTIONS} />
        </Grid>
      </DemoCard>

      <DemoCard title="Action" hint="the right-hand button submits the value">
        <Grid cols={2}>
          <Input
            label="Subscribe"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            onAction={(value) => window.alert(`Action: ${value}`)}
          />
          <Input
            type="email"
            label="Email"
            defaultValue="test@example.com"
            onAction={(value) => window.alert(`Submit email: ${value}`)}
          />
        </Grid>
      </DemoCard>

      <DemoCard title="States">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[var(--space-ui-medium-m)]">
          <Input label="Default" />
          <Input label="Filled" defaultValue="Has value" />
          <Input label="Error" error="Error message" defaultValue="Invalid" />
          <Input label="Disabled" disabled defaultValue="Disabled" />
        </div>
      </DemoCard>

      <DemoCard title="Helper text">
        <Grid cols={2}>
          <Input label="Postnummer" helperText="4 cifre" defaultValue="2100" />
          <Input label="Navnetryk" helperText="Max 16 tegn" maxLength={16} />
        </Grid>
      </DemoCard>
    </ComponentPage>
  );
}
